'use client'

import React, { useCallback, useMemo } from 'react'

import * as DialogPrimitive from '@radix-ui/react-dialog'
import {
  createFormHook,
  createFormHookContexts,
  revalidateLogic,
  useStore,
} from '@tanstack/react-form'
import { HiXMark } from 'react-icons/hi2'
import { LuBox } from 'react-icons/lu'
import { twMerge } from 'tailwind-merge'
import { z } from 'zod'

import Button from '@/components/common/Button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/common/Dialog'
import IconButton from '@/components/common/IconButton'
import { Separator } from '@/components/common/Separator'
import Spinner from '@/components/common/Spinner'
import { Switch } from '@/components/common/SwitchV2'
import {
  getHtmlFromJsonContent,
  renderAsComponent,
} from '@/components/common/TextArea/RichTextArea'
import { FormInput } from '@/components/common/TextInputV2'
import ErrorMessage from '@/components/form/ErrorMessage'
import Label from '@/components/form/Label'
import { RichTextDescriptionField } from '@/components/form/RichTextDescriptionField'
import type { AnyTanstackAppForm } from '@/components/form/tanstackFormTypes'
import * as EditField from '@/components/library-v2/catalog/ui/Field/Edit'
import {
  buildDefaultValuesFromExtendedSchema,
  type ExtendedValuesZodObject,
  prepareExtendedValuesZodObject,
} from '@/components/pursuits/shared/extendedSchemaFields'

import {
  getStoredExtendedValues,
  isExtendedFieldEdited,
  mergeEditedExtendedValues,
} from '@/helpers/extended-schema'

import {
  useEntitySchema,
  useEntitySchemas,
} from '@/hooks/library/useEntitySchemas'
import { useUpdateCatalogEntry } from '@/hooks/library/useUpdateCatalogEntry'

import type { PaginatedResponse } from '@/types/Api'
import type { EntityCatalogEntry } from '@/types/library/Entity'
import type {
  EntityExtendedJsonSchema,
  EntityExtendedJsonSchemaDerivedField,
  JsonValue,
} from '@/types/library/ExtendedSchema'

interface EditEntryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  entry: EntityCatalogEntry
}

// region: zod schema generation for dynamic fields

const entryFormShape: Record<
  Exclude<keyof EditEntryFormValues, 'values'>,
  z.ZodTypeAny
> = {
  name: z.string().min(1, 'Name is required'),
  description: z.string().nullable().or(z.literal('')),
  archived: z.boolean(),
}

const buildEntryFormZodObject = (
  values: ExtendedValuesZodObject,
): z.ZodObject<Record<keyof EditEntryFormValues, z.ZodTypeAny>> =>
  z.object({ ...entryFormShape, values })

// endregion

interface EditEntryFormValues {
  name: string
  description: string
  archived: boolean
  values: Record<string, JsonValue>
}

export const { fieldContext, useFieldContext, formContext, useFormContext } =
  createFormHookContexts()

// region: description field

export const DescriptionFieldControl: React.FC = () => {
  const field = useFieldContext<string | undefined>()

  const error = EditField.formatErrorMessage(
    useStore(field.store, (s) => s.meta.errors),
  )

  return (
    <RichTextDescriptionField
      value={field.state.value}
      onChange={field.handleChange}
      error={error}
    />
  )
}

interface RenderedDescriptionProps {
  value: string
}

export const RenderedDescription: React.FC<RenderedDescriptionProps> = ({
  value,
}) => {
  return renderAsComponent(value)
}

// endregion: description field

export const NameFieldControl: React.FC = () => {
  const field = useFieldContext<string | undefined>()
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      field.handleChange(e.target.value),
    [field],
  )
  const error = EditField.formatErrorMessage(
    useStore(field.store, (s) => s.meta.errors),
  )
  return (
    <>
      <FormInput
        value={field.state.value}
        onChange={handleChange}
        onBlur={field.handleBlur}
        placeholder="Enter a name..."
        className="dark:bg-default-dark"
        aria-invalid={!!error}
      />
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </>
  )
}

export const ArchivedFieldControl: React.FC = () => {
  const field = useFieldContext<boolean>()
  const error = EditField.formatErrorMessage(
    useStore(field.store, (s) => s.meta.errors),
  )

  const handleChange = useCallback(
    () => field.handleChange(!field.state.value),
    [field],
  )

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="text-sm text-primary-900 dark:text-primary-100 font-medium">
          Active
        </span>
        <Switch checked={!field.state.value} onClick={handleChange} />
      </div>
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </div>
  )
}

// Register components with the form hook
// Build production-quality form field components by adapting reusable base components
const adaptedFieldComponents = EditField.createTanstackFormFieldComponents({
  useFieldContext,
})

const { useAppForm } = createFormHook({
  fieldContext,
  formContext,
  fieldComponents: {
    ...adaptedFieldComponents,
    NameFieldControl,
    DescriptionFieldControl,
    ArchivedFieldControl,
  },
  formComponents: {},
})

interface OverviewSectionProps {
  form: AnyTanstackAppForm
}

const OverviewSection: React.FC<OverviewSectionProps> = ({ form }) => {
  return (
    <div className="flex flex-col gap-5">
      <form.AppField name="name">
        {(field) => (
          <div className="flex flex-col gap-2">
            <Label required>Name</Label>
            <field.NameFieldControl />
          </div>
        )}
      </form.AppField>

      <form.AppField name="description">
        {(field) => (
          <div className="flex flex-col gap-2">
            <Label>Description</Label>
            <field.DescriptionFieldControl />
          </div>
        )}
      </form.AppField>

      <div className="flex items-center gap-2">
        <form.AppField name="archived">
          {(field) => <field.ArchivedFieldControl />}
        </form.AppField>
      </div>
    </div>
  )
}

interface DynamicFieldListProps {
  snapshot: EntityExtendedJsonSchema
  form: AnyTanstackAppForm
}

const DynamicFieldList: React.FC<DynamicFieldListProps> = ({
  snapshot,
  form,
}) => {
  const sortedFields = useMemo(
    () =>
      [...snapshot.fields].sort((a, b) => {
        const aOrder = a.order ?? null
        const bOrder = b.order ?? null
        if (aOrder !== null && bOrder !== null) {
          return aOrder - bOrder
        } else if (aOrder !== null) {
          return -1
        } else if (bOrder !== null) {
          return 1
        }
        return a.name.localeCompare(b.name)
      }),
    [snapshot.fields],
  )

  const renderField = useCallback(
    (field: EntityExtendedJsonSchemaDerivedField) => {
      if (field.id === 'description') {
        return null
      }
      const fieldName = `values.${field.id}` as const

      return (
        <form.AppField key={field.id} name={fieldName}>
          {(f) => {
            switch (field.type) {
              case 'string':
                return (
                  <f.StringFieldControl
                    label={field.name}
                    required={field.required}
                  />
                )
              case 'number':
                return (
                  <f.NumberFieldControl
                    label={field.name}
                    required={field.required}
                    min={field.ge ?? undefined}
                    max={field.le ?? undefined}
                  />
                )
              case 'boolean':
                return (
                  <f.BooleanFieldControl
                    label={field.name}
                    required={field.required}
                  />
                )
              case 'dropdown':
                return (
                  <f.DropdownFieldControl
                    label={field.name}
                    required={field.required}
                    options={field.options}
                  />
                )
              case 'multi_select':
                return (
                  <f.MultiSelectFieldControl
                    label={field.name}
                    required={field.required}
                    options={field.options}
                  />
                )
              case 'string_list':
                return (
                  <f.StringListChipsFieldControl
                    label={field.name}
                    required={field.required}
                  />
                )
              case 'number_list':
                return (
                  <f.NumberListChipsFieldControl
                    label={field.name}
                    required={field.required}
                  />
                )
              case 'date':
                return (
                  <f.DateFieldControl
                    label={field.name}
                    required={field.required}
                  />
                )
              case 'currency':
                return (
                  <f.CurrencyFieldControl
                    label={field.name}
                    required={field.required}
                    currency={field.currency}
                    min={field.ge ?? undefined}
                    max={field.le ?? undefined}
                  />
                )
              default:
                return null
            }
          }}
        </form.AppField>
      )
    },
    [form],
  )

  return (
    <div className="flex flex-col gap-8">{sortedFields.map(renderField)}</div>
  )
}

// Shared form component used by both Dialog and Drawer variants
interface EditEntryFormProps {
  entry: EntityCatalogEntry
  onClose: () => void
  bodyClassName?: string
  footerClassName?: string
}

const EditEntryForm: React.FC<EditEntryFormProps> = ({
  entry,
  onClose,
  bodyClassName = 'flex flex-col gap-6 my-5',
  footerClassName = 'items-center justify-end',
}) => {
  const { data: schema, isLoading } = useEntitySchema({
    orgId: entry.organization_id,
    schemaId: entry.extended_schema_id ?? '',
  })

  const selectDefaultDynamicValues = useCallback(
    (
      data: PaginatedResponse<EntityExtendedJsonSchema>,
    ): Record<string, JsonValue> => {
      const schema = data.data.find((s) => s.id === entry.extended_schema_id)
      if (!schema) return {} as Record<string, JsonValue>
      return buildDefaultValuesFromExtendedSchema(
        schema,
        getStoredExtendedValues(entry.extended_data),
      )
    },
    [entry.extended_schema_id, entry],
  )

  const { data: defaultDynamicValues } = useEntitySchemas({
    orgId: entry.organization_id,
    select: selectDefaultDynamicValues,
  })

  const { mutateAsync: updateEntry, isPending } = useUpdateCatalogEntry({
    orgId: entry.organization_id,
    optimisticUpdate: false,
    invalidateOnSuccess: true,
  })

  const storedValues = useMemo(
    () => getStoredExtendedValues(entry.extended_data),
    [entry.extended_data],
  )
  const extendedValuesZodObject = useMemo(
    () =>
      prepareExtendedValuesZodObject(
        (schema?.fields ?? []).filter(
          (field) => field.id !== 'name' && field.id !== 'description',
        ),
        storedValues,
      ),
    [schema, storedValues],
  )

  const defaultValues: EditEntryFormValues = useMemo(
    () => ({
      name: entry.name ?? '',
      description: entry.description ?? '',
      archived: entry.archived,
      values: defaultDynamicValues ?? {},
    }),
    [entry.id, defaultDynamicValues],
  )

  const handleSubmit = useCallback(
    async (
      value: EditEntryFormValues,
      isEdited: (fieldId: string) => boolean,
    ) => {
      // Convert EntityCatalogEntry to CatalogEntry format for the update hook
      const updated: EntityCatalogEntry = {
        id: entry.id,
        organization_id: entry.organization_id,
        name: value.name,
        description: value.description ?? null,
        created_at: entry.created_at,
        last_modified: entry.last_modified,
        archived: value.archived,
        archived_at: entry.archived_at,
        archived_by: entry.archived_by,
        extended_schema_id: entry.extended_schema_id,
        extended_data: mergeEditedExtendedValues(
          storedValues,
          value.values,
          isEdited,
        ),
        entity_type: entry.entity_type,
        last_referenced_at: entry.last_referenced_at,
        owner_user_id: entry.owner_user_id,
        priority: entry.priority,
        relationship_status: entry.relationship_status,
        last_contacted_at: entry.last_contacted_at,
        created_by_user_id: entry.created_by_user_id,
        confirmed_by_user_id: entry.confirmed_by_user_id,
        confirmed: entry.confirmed,
        tag_ids: entry.tag_ids,
      }
      try {
        await updateEntry(updated)
        onClose()
      } catch (e) {
        console.error(e)
      }
    },
    [entry, storedValues, updateEntry, onClose],
  )

  const form = useAppForm({
    defaultValues,
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'change',
    }),
    validators: {
      onDynamic: ({ formApi }) =>
        schema
          ? formApi.parseValuesWithSchema(
              // @ts-expect-error - a zod object built at runtime isn't typed as the form values
              buildEntryFormZodObject(
                extendedValuesZodObject(isExtendedFieldEdited(formApi)),
              ),
            )
          : undefined,
    },
    onSubmit: ({ value, formApi }) =>
      handleSubmit(value, isExtendedFieldEdited(formApi)),
  })
  const formView = form as unknown as AnyTanstackAppForm

  const numErrors = useStore(form.store, (s) => s.errors.length)

  const handleCancel = useCallback(() => {
    onClose()
  }, [onClose])

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <form.AppForm>
        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <Spinner />
          </div>
        ) : schema ? (
          <>
            <div className={twMerge('flex-1 min-h-0', bodyClassName)}>
              <OverviewSection form={formView} />

              <Separator />

              {/* <div className="flex items-center gap-x-2">
                <LuBox
                  size={16}
                  className="text-primary-500 dark:text-primary-400"
                />
                <h2 className="text-sm font-medium text-primary-900 dark:text-primary-100">
                  Specifications
                </h2>
              </div> */}
              <DynamicFieldList snapshot={schema} form={formView} />
            </div>
            <div className={twMerge('w-full', footerClassName)}>
              <div className="flex-1">
                {numErrors > 0 && (
                  <ErrorMessage className="self-center">
                    Please review and correct the fields with errors.
                  </ErrorMessage>
                )}
              </div>
              <Button
                type="button"
                variant="outline-compact"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                variant="primary-compact"
                disabled={isPending}
                isLoading={isPending}
                onClick={form.handleSubmit}
              >
                Save Changes
              </Button>
            </div>
          </>
        ) : (
          <div
            className={twMerge(
              'flex-1 flex items-center justify-center py-10',
              bodyClassName,
            )}
          >
            <span className="text-sm text-red-600 dark:text-red-500">
              No schema found for this entry.
            </span>
          </div>
        )}
      </form.AppForm>
    </div>
  )
}

export const EditEntryDialog: React.FC<EditEntryDialogProps> = ({
  open,
  onOpenChange,
  entry,
}) => {
  const handleClose = useCallback(() => {
    onOpenChange(false)
  }, [onOpenChange])
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className="sm:min-w-[700px] max-w-[700px] p-5"
      >
        <DialogHeader>
          <DialogTitle>Edit Entry</DialogTitle>
        </DialogHeader>
        <EditEntryForm entry={entry} onClose={handleClose} />
      </DialogContent>
    </Dialog>
  )
}

// A right-side drawer variant using the same form/content
export const EditEntryDrawer: React.FC<EditEntryDialogProps> = ({
  open,
  onOpenChange,
  entry,
}) => {
  const handleClose = useCallback(() => {
    onOpenChange(false)
  }, [onOpenChange])
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-black/20 dark:bg-black/40 !duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={twMerge(
            'fixed inset-y-0 top-4 right-2 z-50 h-[calc(100vh-2rem)] w-[700px] max-w-[90vw]',
            'border-l border-primary-200 dark:border-primary-800 bg-white dark:bg-primary-900',
            'mr-2 rounded-lg outline-none',
            'flex flex-col',
            'divide-y divide-primary-200 dark:divide-primary-800',
            '!duration-300',
            'ease-out data-[state=open]:animate-in data-[state=open]:slide-in-from-right data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right',
          )}
        >
          <DialogHeader className="relative flex justify-center items-between px-5 h-14 flex-shrink-0">
            <DialogTitle>Edit Entry</DialogTitle>
            <div className="absolute right-4 top-2">
              <DialogPrimitive.Close asChild={true}>
                <IconButton>
                  <HiXMark className="h-[18px] w-[18px] text-primary-900 dark:text-primary-100" />
                  <span className="sr-only">Close</span>
                </IconButton>
              </DialogPrimitive.Close>
            </div>
          </DialogHeader>
          <EditEntryForm
            entry={entry}
            onClose={handleClose}
            bodyClassName="flex flex-col gap-6 px-5 py-4 overflow-y-auto"
            footerClassName="flex flex-shrink-0 items-center justify-between gap-2 px-5 h-14 border-t border-primary-200 dark:border-primary-800"
          />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
